import { AudioLines, FileText, Film, Images, Loader2, Upload, X } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import type { MentionItem } from "@/components/prompt-editor/types"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import type { LibraryProjectChoice } from "@/components/library/library-picker-dialog"
import { attachLibraryPicks, TaskLibraryPicker } from "@/components/tasks/task-file-attach-menu"
import { TaskFileDropOverlay } from "@/components/tasks/task-file-drop-overlay"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import type { LibraryPickResult } from "@/hooks/use-library-pick-selection"
import { useMultiFileUpload } from "@/hooks/use-multi-file-upload"
import { useTranslation } from "@/hooks/use-translation"
import { formatBytes } from "@/lib/format-bytes"
import {
  librarySourceKey,
  projectAssetSource,
  projectLibraryUploadSource,
  projectSketchSource,
  projectUploadSource,
} from "@/lib/library-source"
import { cn } from "@/lib/utils"
import type { ShotFrameSource } from "@/lib/workspace-api"
import type { TaskFile } from "@/types/tasks"

export function taskFileMentionItems(files: TaskFile[]): MentionItem[] {
  return files.map((file) => ({
    category: "file",
    id: file.blob_id,
    label: file.name,
    imageUrl: file.thumb_url,
    mediaType: file.media,
  }))
}

export function taskDescriptionMentionsFile(description: string, blobId: number): boolean {
  return description.includes(`@file[${blobId}]`)
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

function TaskFileThumb({ file }: { file: TaskFile }) {
  const Icon = file.media === "video" ? Film : file.media === "audio" ? AudioLines : FileText
  return (
    <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md border border-ui-border bg-ui-surface-sunken text-ui-text-muted">
      {file.thumb_url ? (
        <img
          src={file.thumb_url}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          className="size-full object-cover"
        />
      ) : (
        <Icon className="size-3.5" aria-hidden />
      )}
    </span>
  )
}

const FILE_SOURCE_BUTTON =
  "flex min-h-8 items-center justify-center gap-2 rounded-lg border border-dashed border-ui-border-strong px-3 py-1.5 text-center text-xs text-ui-text-muted transition-colors hover:border-ui-accent hover:text-ui-link focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"

type TaskFilesProps = {
  files: TaskFile[]
  // Без проекта (сводная доска, проект ещё не выбран) Библиотека неактивна,
  // пока chooseLibraryProject не откроет выбор проекта внутри диалога.
  projectId: number | null
  chooseLibraryProject?: boolean
  libraryProjects?: readonly LibraryProjectChoice[]
  libraryProjectsLoading?: boolean
  onLibraryProjectChange?: (projectId: number) => void
  frameFormat: string
  canAttach: boolean
  canRemove: boolean
  removingBlobId: number | null
  onOpen: (file: TaskFile) => void
  onRemove: (file: TaskFile) => void
  onUpload: (file: File) => Promise<void>
  onAttachFromLibrary: (source: ShotFrameSource) => Promise<void>
  // Причина не загружать файл (лимиты черновика); null — можно.
  validateUpload?: (file: File) => string | null
  onLibraryOpenChange: (open: boolean) => void
  onBusyChange?: (busy: boolean) => void
  dropLabel: string
  // Выключается, пока поверх панели открыто другое окно.
  dropEnabled: boolean
}

export function TaskFiles({
  files,
  projectId,
  chooseLibraryProject = false,
  libraryProjects,
  libraryProjectsLoading = false,
  onLibraryProjectChange,
  frameFormat,
  canAttach,
  canRemove,
  removingBlobId,
  onOpen,
  onRemove,
  onUpload,
  onAttachFromLibrary,
  validateUpload,
  onLibraryOpenChange,
  onBusyChange,
  dropLabel,
  dropEnabled,
}: TaskFilesProps) {
  const { t } = useTranslation("tasks")
  const [attaching, setAttaching] = useState(false)
  const [queued, setQueued] = useState(0)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const uploadQueueRef = useRef<Promise<void>>(Promise.resolve())

  const { fileRef, uploading, handleFiles, inputProps } = useMultiFileUpload({
    uploadOne: onUpload,
    stopOnError: false,
    validateFile: (file) => {
      const problem = validateUpload?.(file) ?? null
      if (problem) toast.error(problem, { id: "task-files-limit" })
      return problem == null
    },
    onError: (error) => toast.error(errorMessage(error, t("files.uploadFailed"))),
  })

  // По id блоба узнаются только загрузки, эскизы и медиа сущностей; повтор остального сервер молча пропустит.
  const pickedKeys = useMemo(
    () =>
      files.flatMap((file) =>
        [projectUploadSource, projectLibraryUploadSource, projectSketchSource, projectAssetSource].map((source) =>
          librarySourceKey(source(file.blob_id)),
        ),
      ),
    [files],
  )

  // Выбор и броски грузятся одной очередью: лимиты черновика считаются по уже загруженным файлам.
  const queueFiles = (batch: File[]) => {
    setQueued((count) => count + 1)
    uploadQueueRef.current = uploadQueueRef.current
      .then(() => handleFiles(batch))
      .catch(() => undefined)
      .finally(() => setQueued((count) => count - 1))
  }

  const changeLibraryOpen = (open: boolean) => {
    setLibraryOpen(open)
    onLibraryOpenChange(open)
  }

  const attachFromLibrary = async (picked: readonly LibraryPickResult[]) => {
    if (attaching) return
    setAttaching(true)
    try {
      const attached = await attachLibraryPicks(picked, onAttachFromLibrary, (names) =>
        t("files.libraryPartial", { names }),
      )
      if (attached) changeLibraryOpen(false)
    } finally {
      setAttaching(false)
    }
  }

  const busy = uploading || attaching
  const working = busy || queued > 0
  const libraryLocked = projectId == null && !chooseLibraryProject

  useEffect(() => {
    onBusyChange?.(working)
  }, [onBusyChange, working])

  if (!canAttach && files.length === 0) return null

  return (
    <section className="mt-4 border-t border-ui-border pt-3">
      <div className="flex h-7 items-center">
        <span className="text-xs leading-4 text-ui-text-muted">{t("files.title")}</span>
      </div>

      {files.length > 0 ? (
        <ul className="-mx-2 mt-1 flex flex-col gap-0.5">
          {files.map((file) => (
            <li
              key={file.blob_id}
              className="group/file flex min-w-0 items-center gap-1 rounded-lg pr-1 hover:bg-ui-surface-raised"
            >
              <button
                type="button"
                title={file.name}
                onClick={() => onOpen(file)}
                className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg py-1.5 pl-2 text-left focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
              >
                <TaskFileThumb file={file} />
                <span className="min-w-0 flex-1 truncate text-xs text-ui-text-primary">{file.name}</span>
                <span className="shrink-0 text-[11px] text-ui-text-muted tabular-nums">
                  {formatBytes(file.byte_size)}
                </span>
              </button>
              {!canRemove ? null : removingBlobId === file.blob_id ? (
                <span className="flex size-6 shrink-0 items-center justify-center text-ui-text-muted">
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={t("files.remove")}
                  title={t("files.remove")}
                  onClick={() => onRemove(file)}
                  className="flex size-6 shrink-0 items-center justify-center rounded-md text-ui-text-muted opacity-0 transition-opacity group-hover/file:opacity-100 hover:text-ui-danger focus-visible:opacity-100"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {canAttach ? (
        <>
          <div className={cn("flex items-stretch gap-2", files.length > 0 ? "mt-2" : "mt-1")}>
            {/* button во flex-ряду не растягивается (Chrome), поэтому высоту держит оболочка. */}
            <div className="flex min-w-0 flex-1">
              <button
                type="button"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                className={cn(FILE_SOURCE_BUTTON, "w-full")}
              >
                {busy ? (
                  <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
                ) : (
                  <Upload className="size-3.5 shrink-0" aria-hidden />
                )}
                {t("files.dropHint")}
              </button>
            </div>
            <div className="flex">
              <ActionHintTooltip
                className="flex h-full"
                label={libraryLocked ? t("dialog.projectRequired") : undefined}
              >
                <button
                  type="button"
                  disabled={busy || libraryLocked}
                  className={cn(FILE_SOURCE_BUTTON, "h-full")}
                  onClick={() => changeLibraryOpen(true)}
                >
                  <Images className="size-3.5 shrink-0" />
                  {t("files.library")}
                </button>
              </ActionHintTooltip>
            </div>
          </div>
          <input ref={fileRef} {...inputProps} onChange={(event) => queueFiles(Array.from(event.target.files ?? []))} />
          {projectId != null || chooseLibraryProject ? (
            <TaskLibraryPicker
              open={libraryOpen}
              onOpenChange={changeLibraryOpen}
              projectId={projectId}
              frameFormat={frameFormat}
              pickedKeys={pickedKeys}
              busy={attaching}
              projectChoices={libraryProjects}
              projectChoicesLoading={libraryProjectsLoading}
              onProjectChange={onLibraryProjectChange}
              onPick={(picked) => void attachFromLibrary(picked)}
            />
          ) : null}
          <TaskFileDropOverlay enabled={dropEnabled} label={dropLabel} onDrop={queueFiles} />
        </>
      ) : null}
    </section>
  )
}

export function TaskFileViewer({
  file,
  projectId,
  onClose,
}: {
  file: TaskFile | null
  projectId: number | null
  onClose: () => void
}) {
  const image = file?.media === "image" ? file : null
  const video = file?.media === "video" ? file : null
  const audio = file?.media === "audio" && projectId != null ? file : null
  const onOpenChange = (open: boolean) => {
    if (!open) onClose()
  }

  return (
    <>
      <ImageLightboxDialog
        open={image != null || video != null}
        onOpenChange={onOpenChange}
        url={image?.url ?? null}
        videoUrl={video?.url ?? null}
        title={file?.name ?? null}
        downloadUrl={image?.url ?? null}
        downloadFilename={image?.name ?? null}
        copyable={image != null}
        nested
      />
      <SketchDialog
        open={audio != null}
        onOpenChange={onOpenChange}
        projectId={projectId ?? 0}
        sourceUrl={null}
        audioUrl={audio?.url ?? null}
        presentation="view"
        allowEdit={false}
        persist="none"
        nested
        headerTitle={audio?.name ?? null}
      />
    </>
  )
}
